import React, { useEffect, useState } from 'react';
import ExcelJS from 'exceljs';
import moment from 'moment';

// assets
import { faDownload, faFileSpreadsheet, faRotate, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// services
import EmployeeLeaveService from '../../../../services/employees/leave';
import ErrorService from '../../../../services/general/error';
import { useStore } from '../../../../stores/root';

// props
import { IEmployeeLeaveMatrixRowProps } from '../../../../props/employees/leaves/leave';

// components
import { CommandBar, Dropdown, ICommandBarItemProps, IDropdownOption, PrimaryButton, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import Text from '../../../typography/text';
import SelectSubsidiary from '../../../uiframeworks/forms/selectSubsidiary';
import ISubsidiariesResourceShort from '../../../../props/data/subsidiaries';

const LeaveMatrix: React.FC = () => {
    const { banner } = useStore();
    const [loaded, setLoaded] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [selectedYear, setSelectedYear] = useState<number>(moment().year());
    const [selectedSubsidiary, setSelectedSubsidiary] = useState<ISubsidiariesResourceShort | undefined>();
    const [matrixData, setMatrixData] = useState<IEmployeeLeaveMatrixRowProps[]>([]);

    useEffect(() => {
        loadData();
    }, [selectedYear, selectedSubsidiary]);

    const loadData = async () => {
        try {
            setLoaded(false);
            let qs = `year=${selectedYear}`;
            if (selectedSubsidiary?.id) {
                qs += `&subsidiaryId=${selectedSubsidiary.id}`;
            }

            const data = await EmployeeLeaveService.getMatrix(qs);
            setMatrixData(data || []);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'matrix_load_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to load leave matrix. Error: ${ErrorService.getMessage(e)}`
            });
            setLoaded(true);
        }
    };

    const handleExportExcel = async () => {
        try {
            setDownloading(true);
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet(`Leave Recap ${selectedYear}`);

            // Title Header
            worksheet.mergeCells('A1:U1');
            const titleRow = worksheet.getCell('A1');
            titleRow.value = `EMPLOYEE LEAVE & WFH RECAPITULATION - YEAR ${selectedYear}`;
            titleRow.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
            titleRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
            titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(1).height = 30;

            // Column Headers
            worksheet.getRow(3).values = [
                'No',
                'Employee Name',
                'Email',
                'Leave Quota',
                'Leave Used',
                'Leave Balance',
                'WFH Quota',
                'WFH Used',
                'WFH Balance',
                'Special Leave',
                'Leave #1',
                'Leave #2',
                'Leave #3',
                'Leave #4',
                'Leave #5',
                'Leave #6',
                'Leave #7',
                'Leave #8',
                'Leave #9',
                'Leave #10',
                'Leave #11',
                'Leave #12',
                'WFH #1',
                'WFH #2',
                'WFH #3'
            ];

            const headerRow = worksheet.getRow(3);
            headerRow.height = 24;
            headerRow.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.eachCell((cell, colNum) => {
                let color = 'FF334155';
                if (colNum >= 4 && colNum <= 6) color = 'FF0284C7'; // Blue for Leave Quota
                if (colNum >= 7 && colNum <= 9) color = 'FF059669'; // Green for WFH Quota
                if (colNum >= 11 && colNum <= 22) color = 'FF475569'; // Slate for Leave 1-12
                if (colNum >= 23) color = 'FF0D9488'; // Teal for WFH 1-3
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // Data Rows
            matrixData.forEach((row, idx) => {
                const annualDates: string[] = Array(12).fill('-');
                row.annualLeaves.slice(0, 12).forEach((l, i) => {
                    annualDates[i] = `${moment(l.startDate).format('DD/MM')} (${l.totalDays}d)`;
                });

                const wfhDates: string[] = Array(3).fill('-');
                row.wfhLeaves.slice(0, 3).forEach((l, i) => {
                    wfhDates[i] = `${moment(l.startDate).format('DD/MM')} (${l.totalDays}d)`;
                });

                const rowValues = [
                    idx + 1,
                    row.employee.name,
                    row.employee.email || '-',
                    row.balance.annualQuota,
                    row.balance.annualUsed,
                    row.balance.annualRemaining,
                    row.balance.wfhQuota,
                    row.balance.wfhUsed,
                    row.balance.wfhRemaining,
                    row.balance.specialUsed,
                    ...annualDates,
                    ...wfhDates
                ];

                const excelRow = worksheet.addRow(rowValues);
                excelRow.height = 20;
                excelRow.eachCell((cell, colNum) => {
                    cell.font = { name: 'Calibri', size: 10 };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                        left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                        bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                        right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
                    };
                    if (colNum === 1 || colNum >= 4) {
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    } else {
                        cell.alignment = { horizontal: 'left', vertical: 'middle' };
                    }
                });
            });

            // Auto fit column width
            worksheet.columns.forEach((col, idx) => {
                if (idx === 1) col.width = 25; // Name
                else if (idx === 2) col.width = 25; // Email
                else if (idx >= 3 && idx <= 9) col.width = 14;
                else col.width = 16;
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Employee_Leave_Matrix_${selectedYear}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
            setDownloading(false);
        } catch (e) {
            setDownloading(false);
            banner.add({
                key: 'export_excel_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to export data to Excel. Error: ${ErrorService.getMessage(e)}`
            });
        }
    };

    const currentYear = moment().year();
    const yearOptions: IDropdownOption[] = [
        { key: currentYear + 1, text: String(currentYear + 1) },
        { key: currentYear, text: String(currentYear) },
        { key: currentYear - 1, text: String(currentYear - 1) },
    ];

    return (
        <Stack tokens={{ childrenGap: 16 }}>
            {/* Filter Bar & Export Button */}
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" wrap tokens={{ childrenGap: 12 }}>
                <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                    <Stack styles={{ root: { minWidth: 120 } }}>
                        <Dropdown
                            label="Calendar Year"
                            selectedKey={selectedYear}
                            options={yearOptions}
                            onChange={(evt, opt) => {
                                if (opt) setSelectedYear(Number(opt.key));
                            }}
                        />
                    </Stack>
                    <Stack styles={{ root: { minWidth: 220 } }}>
                        <SelectSubsidiary
                            label="Filter Subsidiary"
                            selected={selectedSubsidiary}
                            onChange={(sub) => setSelectedSubsidiary(sub)}
                        />
                    </Stack>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <PrimaryButton
                        iconProps={{ iconName: 'Download' }}
                        text={downloading ? 'Exporting...' : 'Export to Excel (.xlsx)'}
                        disabled={downloading || matrixData.length === 0}
                        onClick={handleExportExcel}
                        style={{ background: '#10b981', borderColor: '#10b981' }}
                    />
                </Stack>
            </Stack>

            {/* Matrix Digital Table */}
            {!loaded ? (
                <Stack horizontalAlign="center" styles={{ root: { padding: '40px 0' } }}>
                    <Spinner size={SpinnerSize.large} label="Loading annual matrix recap..." />
                </Stack>
            ) : matrixData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                    <Text size="medium">No employee data found for year {selectedYear}.</Text>
                </div>
            ) : (
                <div style={{
                    overflowX: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 12,
                        minWidth: 1200
                    }}>
                        <thead>
                            <tr style={{ background: '#f1f5f9', color: '#475569', borderBottom: '2px solid #cbd5e1' }}>
                                <th style={{ padding: '10px 12px', textAlign: 'center', width: 40 }}>#</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left', minWidth: 180 }}>Employee Name</th>
                                <th style={{ padding: '10px 8px', textAlign: 'center', background: '#e0f2fe', color: '#0369a1' }}>Leave Quota</th>
                                <th style={{ padding: '10px 8px', textAlign: 'center', background: '#e0f2fe', color: '#0369a1' }}>Used</th>
                                <th style={{ padding: '10px 8px', textAlign: 'center', background: '#e0f2fe', color: '#0369a1' }}>Balance</th>
                                <th style={{ padding: '10px 8px', textAlign: 'center', background: '#dcfce7', color: '#15803d' }}>WFH Quota</th>
                                <th style={{ padding: '10px 8px', textAlign: 'center', background: '#dcfce7', color: '#15803d' }}>Used</th>
                                <th style={{ padding: '10px 8px', textAlign: 'center', background: '#dcfce7', color: '#15803d' }}>Balance</th>
                                <th style={{ padding: '10px 8px', textAlign: 'center', background: '#f3e8ff', color: '#7e22ce' }}>Special</th>
                                <th style={{ padding: '10px 12px', textAlign: 'center', minWidth: 320 }}>Annual Leave Details (1 to 12)</th>
                                <th style={{ padding: '10px 12px', textAlign: 'center', minWidth: 200 }}>WFH Details (1 to 3)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {matrixData.map((row, idx) => (
                                <tr key={row.employee.id} style={{
                                    borderBottom: '1px solid #f1f5f9',
                                    background: idx % 2 === 0 ? '#fff' : '#fafafa'
                                }}>
                                    <td style={{ padding: '10px 12px', textAlign: 'center', color: '#94a3b8' }}>{idx + 1}</td>
                                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b' }}>
                                        {row.employee.name}
                                        {row.employee.email && (
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>{row.employee.email}</div>
                                        )}
                                    </td>

                                    {/* Leave Quota */}
                                    <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600 }}>{row.balance.annualQuota}</td>
                                    <td style={{ padding: '10px 8px', textAlign: 'center', color: '#dc2626', fontWeight: 600 }}>{row.balance.annualUsed}</td>
                                    <td style={{ padding: '10px 8px', textAlign: 'center', color: '#0284c7', fontWeight: 700 }}>{row.balance.annualRemaining}</td>

                                    {/* WFH Quota */}
                                    <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600 }}>{row.balance.wfhQuota}</td>
                                    <td style={{ padding: '10px 8px', textAlign: 'center', color: '#dc2626', fontWeight: 600 }}>{row.balance.wfhUsed}</td>
                                    <td style={{ padding: '10px 8px', textAlign: 'center', color: '#10b981', fontWeight: 700 }}>{row.balance.wfhRemaining}</td>

                                    {/* Special Leaves */}
                                    <td style={{ padding: '10px 8px', textAlign: 'center', color: '#8b5cf6', fontWeight: 600 }}>{row.balance.specialUsed} d</td>

                                    {/* Leave 1-12 Pills */}
                                    <td style={{ padding: '10px 12px' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                            {row.annualLeaves.length === 0 ? (
                                                <span style={{ color: '#cbd5e1' }}>No leave taken</span>
                                            ) : (
                                                row.annualLeaves.map((l, i) => (
                                                    <span
                                                        key={l.id}
                                                        title={`${l.reason || 'Leave'} (${moment(l.startDate).format('DD/MM/YYYY')} - ${moment(l.endDate).format('DD/MM/YYYY')})`}
                                                        style={{
                                                            background: '#e0f2fe',
                                                            color: '#0369a1',
                                                            borderRadius: 4,
                                                            padding: '2px 6px',
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            display: 'inline-block'
                                                        }}
                                                    >
                                                        #{i + 1}: {moment(l.startDate).format('DD/MM')} ({l.totalDays}d)
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                    </td>

                                    {/* WFH 1-3 Pills */}
                                    <td style={{ padding: '10px 12px' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                            {row.wfhLeaves.length === 0 ? (
                                                <span style={{ color: '#cbd5e1' }}>No WFH taken</span>
                                            ) : (
                                                row.wfhLeaves.map((l, i) => (
                                                    <span
                                                        key={l.id}
                                                        title={`${l.reason || 'WFH'} (${moment(l.startDate).format('DD/MM/YYYY')} - ${moment(l.endDate).format('DD/MM/YYYY')})`}
                                                        style={{
                                                            background: '#dcfce7',
                                                            color: '#15803d',
                                                            borderRadius: 4,
                                                            padding: '2px 6px',
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            display: 'inline-block'
                                                        }}
                                                    >
                                                        WFH #{i + 1}: {moment(l.startDate).format('DD/MM')}
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Stack>
    );
};

export default LeaveMatrix;
